import { normalizeRotation } from "./constructionRotation";
import {
  tracingImageCenter,
  tracingImageRotation,
  type TracingImage,
} from "./Project";
import type { FlipAxis } from "./constructionFlip";

/**
 * Pure (no Paper) geometry for the construction Transform bar (issue #346)
 * applied to tracing images (#545 — make images behave like any other
 * construction object). Parallels `flipConstructionLine` /
 * `rotateConstructionLine`, but a tracing image's representation is a placed
 * rectangle (`x`/`y`/`widthMm`/`heightMm`) plus a centre `rotation`, an
 * optional horizontal `mirrored` flag, and a source-image `quad`, so the
 * transforms are closed-form trig rather than a pathData rewrite.
 */

/**
 * Mirror a tracing image in place about a pivot (the Flip buttons).
 * "horizontal" reflects across the vertical line `x = cx` (left ⇄ right);
 * "vertical" reflects across `y = cy` (top ⇄ bottom).
 *
 * The reflection is expressed entirely through the image's canonical
 * placement transform `T(centre) ∘ R(rotation) ∘ Mirror`, so the *rendered
 * pixels* flip — not just the rectangle. Pre-multiplying an external
 * reflection collapses back into the same form:
 *   - both axes toggle the local horizontal `mirrored` flag (the render
 *     reflects the raster about its centre, working for the warp *and* the
 *     stored-final-copy bitmap alike);
 *   - horizontal negates the rotation (`θ → −θ`) and reflects the centre's x;
 *   - vertical maps the rotation to `180 − θ` and reflects the centre's y
 *     (a vertical flip = horizontal mirror + half turn).
 * `widthMm`/`heightMm` and the source `quad` are untouched — a reflection
 * preserves the axis-aligned rectangle and the bitmap's crop.
 */
export function flipTracingImage(
  image: TracingImage,
  axis: FlipAxis,
  cx: number,
  cy: number,
): TracingImage {
  const c = tracingImageCenter(image);
  const theta = tracingImageRotation(image);
  const horizontal = axis === "horizontal";
  const nc = {
    x: horizontal ? 2 * cx - c.x : c.x,
    y: horizontal ? c.y : 2 * cy - c.y,
  };
  const next: TracingImage = {
    ...image,
    x: nc.x - image.widthMm / 2,
    y: nc.y - image.heightMm / 2,
    rotation: normalizeRotation(horizontal ? -theta : 180 - theta),
    mirrored: !image.mirrored,
  };
  // The custom rotation pivot (#420) reflects across the same flip axis so
  // it stays attached to the image — mirrors `flipConstructionLine`.
  if (image.pivotPoint) {
    next.pivotPoint = {
      x: horizontal ? 2 * cx - image.pivotPoint.x : image.pivotPoint.x,
      y: horizontal ? image.pivotPoint.y : 2 * cy - image.pivotPoint.y,
    };
  }
  return next;
}

/**
 * Rotate a tracing image by `deg` (±90 for the rotate-left / rotate-right
 * buttons) about an external pivot `(cx, cy)`. The centre orbits the pivot and
 * the image's own `rotation` accumulates `deg`; `widthMm`/`heightMm` are *not*
 * swapped — rotation is applied about the centre at render time, so a 90°-turned
 * image keeps its stored un-rotated rectangle. The `mirrored` flag and `quad`
 * ride along untouched (a pure rotation neither mirrors nor re-crops content).
 */
export function rotateTracingImage(
  image: TracingImage,
  deg: number,
  cx: number,
  cy: number,
): TracingImage {
  const c = tracingImageCenter(image);
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const nc = {
    x: cx + (c.x - cx) * cos - (c.y - cy) * sin,
    y: cy + (c.x - cx) * sin + (c.y - cy) * cos,
  };
  const next: TracingImage = {
    ...image,
    x: nc.x - image.widthMm / 2,
    y: nc.y - image.heightMm / 2,
    rotation: normalizeRotation(tracingImageRotation(image) + deg),
  };
  // Orbit the custom rotation pivot (#420) about the same external point so
  // it rides along with the image (e.g. a group/rotate-90 turn). A rotation
  // about the image's *own* pivot leaves it fixed, since it's the centre.
  if (image.pivotPoint) {
    next.pivotPoint = {
      x: cx + (image.pivotPoint.x - cx) * cos - (image.pivotPoint.y - cy) * sin,
      y: cy + (image.pivotPoint.x - cx) * sin + (image.pivotPoint.y - cy) * cos,
    };
  }
  return next;
}
